import React, { useState, useEffect, useMemo } from 'react';
import { adminAPI } from '../../services/api';

// Admin review queue for restaurant-initiated changes held for approval
// (menu edit/add/delete, bank details, profile). Approve → applied; Reject → discarded.
// Backend gate: Settings.restaurantChangeApproval.enabled. No app release.

const TYPE_LABEL = {
  menu_update: '🍽️ Menu edit',
  menu_add: '➕ New menu item',
  menu_delete: '🗑️ Delete menu item',
  bank_details: '🏦 Bank details',
  profile: '🏪 Profile',
};

const maskAccount = (v) => {
  const s = String(v == null ? '' : v);
  return s.length > 4 ? `••••${s.slice(-4)}` : s;
};
const fmt = (key, v) => {
  if (v == null || v === '') return '—';
  if (key === 'accountNumber') return maskAccount(v);
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

function ChangeRequestManagement() {
  const [requests, setRequests] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [gateEnabled, setGateEnabled] = useState(false);
  const [togglingGate, setTogglingGate] = useState(false);
  const [menuExempt, setMenuExempt] = useState([]);       // [{_id, name}] exempt from menu approval
  const [allRestaurants, setAllRestaurants] = useState([]); // for the add-exemption picker
  const [exemptSel, setExemptSel] = useState('');
  const [exemptBusy, setExemptBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [typeFilter, setTypeFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, typeFilter]);

  // Load restaurants once for the exemption picker (admin set, ~small).
  useEffect(() => {
    (async () => {
      try {
        const r = await adminAPI.getAllRestaurants();
        const list = r.data?.data?.restaurants || [];
        setAllRestaurants(
          list.map(x => ({ _id: String(x._id), name: x.name || '(unnamed)' }))
              .sort((a, b) => a.name.localeCompare(b.name))
        );
      } catch { /* picker is optional — exemptions still manageable via list */ }
    })();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getChangeRequests({ status: statusFilter, type: typeFilter || undefined });
      const data = res.data?.data || {};
      setRequests(Array.isArray(data.requests) ? data.requests : []);
      setPendingCount(data.pendingCount || 0);
      setGateEnabled(!!(data.gate && data.gate.enabled));
      setMenuExempt(Array.isArray(data.gate?.menuExemptRestaurants) ? data.gate.menuExemptRestaurants : []);
      setError('');
    } catch (e) {
      setError('Failed to load change requests');
    } finally {
      setLoading(false);
    }
  };

  const act = async (id, kind) => {
    const note = kind === 'reject' ? (window.prompt('Reason for rejection (optional):') ?? '') : '';
    if (kind === 'reject' && note === null) return;
    setBusyId(id);
    try {
      if (kind === 'approve') await adminAPI.approveChangeRequest(id, note);
      else await adminAPI.rejectChangeRequest(id, note);
      await load();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.message || e.message));
    } finally {
      setBusyId(null);
    }
  };

  const diffRows = (cr) => {
    // For adds: show payload fields; deletes: show before; edits/bank/profile: before→after.
    const src = cr.type === 'menu_delete' ? (cr.before || {}) : (cr.payload || {});
    const keys = Object.keys(src).filter(k => k !== 'restaurantId' && k !== 'stats' && k !== 'updatedAt' && k !== 'verificationDisabled');
    return keys.map(k => {
      const before = cr.before ? cr.before[k] : undefined;
      const after = cr.type === 'menu_delete' ? undefined : (cr.payload ? cr.payload[k] : undefined);
      return { k, before, after };
    });
  };

  const toggleGate = async () => {
    const next = !gateEnabled;
    if (!window.confirm(next
      ? 'Turn the approval gate ON? Restaurant menu/bank/profile changes will then require admin approval before applying.'
      : 'Turn the approval gate OFF? Restaurant changes will apply instantly again.')) return;
    setTogglingGate(true);
    try {
      await adminAPI.updateChangeApprovalSettings({ enabled: next });
      setGateEnabled(next);
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.message || e.message));
    } finally {
      setTogglingGate(false);
    }
  };

  const addExempt = async () => {
    if (!exemptSel) return;
    setExemptBusy(true);
    try {
      await adminAPI.updateChangeApprovalSettings({ addMenuExempt: exemptSel });
      setExemptSel('');
      await load();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.message || e.message));
    } finally {
      setExemptBusy(false);
    }
  };

  const removeExempt = async (id) => {
    setExemptBusy(true);
    try {
      await adminAPI.updateChangeApprovalSettings({ removeMenuExempt: id });
      await load();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.message || e.message));
    } finally {
      setExemptBusy(false);
    }
  };

  // Client-side restaurant-name filter over the loaded set (status/type are
  // server-filtered). Case-insensitive substring on the denormalized name.
  const list = useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    if (!q) return requests;
    return requests.filter((cr) => String(cr.restaurantName || '').toLowerCase().includes(q));
  }, [requests, nameFilter]);

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: 24 }}>
      <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0F172A', margin: 0 }}>
        Change Requests {pendingCount > 0 && <span style={chip('#FEF3C7', '#92400E')}>{pendingCount} pending</span>}
      </h1>
      <p style={{ color: '#64748B', fontSize: 14, margin: '6px 0 12px' }}>
        Restaurant changes waiting for approval. Approving applies the change; rejecting discards it.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, padding: '10px 14px', border: '1px solid #E2E8F0', borderRadius: 10, background: gateEnabled ? '#F0FDF4' : '#FFF7ED' }}>
        <span style={{ fontWeight: 700, color: '#0F172A' }}>Approval gate:</span>
        <span style={chip(gateEnabled ? '#DCFCE7' : '#FEE2E2', gateEnabled ? '#166534' : '#B91C1C')}>{gateEnabled ? 'ON' : 'OFF'}</span>
        <span style={{ fontSize: 13, color: '#64748B', flex: 1 }}>
          {gateEnabled
            ? 'Menu (edit/add/delete), bank details & profile changes require approval before applying.'
            : 'Restaurant changes apply instantly. Turn on to require approval.'}
        </span>
        <button onClick={toggleGate} disabled={togglingGate} style={btn(gateEnabled ? '#EF4444' : '#16A34A')}>
          {togglingGate ? '…' : (gateEnabled ? 'Turn OFF' : 'Turn ON')}
        </button>
      </div>

      {/* Per-restaurant menu-approval exemptions */}
      <div style={{ marginBottom: 18, padding: '12px 14px', border: '1px solid #E2E8F0', borderRadius: 10, background: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, color: '#0F172A' }}>🍽️ Menu-approval exemptions</span>
          <span style={chip('#EEF2FF', '#4338CA')}>{menuExempt.length}</span>
        </div>
        <p style={{ fontSize: 13, color: '#64748B', margin: '6px 0 10px' }}>
          These restaurants’ <b>menu</b> changes (add / edit / delete) apply <b>instantly</b>, bypassing approval — even while the gate is ON. Their bank &amp; profile changes are still gated.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          {menuExempt.length === 0 ? (
            <span style={{ fontSize: 13, color: '#94A3B8' }}>No exemptions — every restaurant needs menu approval while the gate is ON.</span>
          ) : menuExempt.map(r => (
            <span key={r._id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#F1F5F9', borderRadius: 16, padding: '4px 6px 4px 12px', fontSize: 13, fontWeight: 600, color: '#334155' }}>
              {r.name}
              <button disabled={exemptBusy} onClick={() => removeExempt(r._id)} title="Remove exemption" style={{ border: 'none', background: '#E2E8F0', color: '#64748B', borderRadius: 12, width: 20, height: 20, cursor: 'pointer', lineHeight: 1, padding: 0 }}>✕</button>
            </span>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select style={{ ...inp, minWidth: 240 }} value={exemptSel} onChange={e => setExemptSel(e.target.value)}>
            <option value="">➕ Add a restaurant to exempt…</option>
            {allRestaurants.filter(r => !menuExempt.some(m => m._id === r._id)).map(r => (
              <option key={r._id} value={r._id}>{r.name}</option>
            ))}
          </select>
          <button onClick={addExempt} disabled={!exemptSel || exemptBusy} style={btn(exemptSel && !exemptBusy ? '#16A34A' : '#94A3B8', true)}>
            {exemptBusy ? '…' : 'Add exemption'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <select style={inp} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="all">All</option>
        </select>
        <select style={inp} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All types</option>
          {Object.keys(TYPE_LABEL).map(t => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
        </select>
        <input
          style={{ ...inp, minWidth: 200, flex: 1 }}
          type="text"
          value={nameFilter}
          onChange={e => setNameFilter(e.target.value)}
          placeholder="🔍 Filter by restaurant name…"
        />
        {nameFilter ? <button onClick={() => setNameFilter('')} style={btn('#94A3B8', true)}>✕ Clear</button> : null}
        <button onClick={load} style={btn('#64748B', true)}>↻ Refresh</button>
      </div>

      {error ? <p style={{ color: '#EF4444' }}>{error}</p> : null}
      {loading ? <p style={{ color: '#64748B' }}>Loading…</p> : list.length === 0 ? (
        <p style={{ padding: 24, color: '#94A3B8', textAlign: 'center', border: '1px solid #E2E8F0', borderRadius: 12 }}>{nameFilter.trim() ? `No ${statusFilter === 'all' ? '' : statusFilter} change requests match “${nameFilter.trim()}”.` : `No ${statusFilter === 'all' ? '' : statusFilter} change requests.`}</p>
      ) : list.map(cr => (
        <div key={cr._id} style={{ border: '1px solid #E2E8F0', borderRadius: 12, padding: 16, marginBottom: 12, background: cr.status === 'pending' ? '#fff' : '#F8FAFC' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <span style={chip('#EEF2FF', '#4338CA')}>{TYPE_LABEL[cr.type] || cr.type}</span>
              <span style={{ fontWeight: 700, color: '#0F172A', marginLeft: 8 }}>{cr.restaurantName || 'Restaurant'}</span>
              {cr.targetLabel ? <span style={{ color: '#64748B', marginLeft: 8 }}>· {cr.targetLabel}</span> : null}
              <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 4 }}>
                by {cr.requestedBy?.name || '—'} {cr.requestedBy?.phone || ''} · {new Date(cr.createdAt).toLocaleString('en-IN')}
                {cr.status !== 'pending' ? <span style={chip(cr.status === 'approved' ? '#DCFCE7' : '#FEE2E2', cr.status === 'approved' ? '#166534' : '#B91C1C')}>{cr.status}</span> : null}
              </div>
            </div>
            {cr.status === 'pending' && (
              <div style={{ display: 'flex', gap: 8 }}>
                <button disabled={busyId === cr._id} onClick={() => act(cr._id, 'approve')} style={btn('#16A34A', true)}>{busyId === cr._id ? '…' : '✓ Approve'}</button>
                <button disabled={busyId === cr._id} onClick={() => act(cr._id, 'reject')} style={btn('#EF4444', true)}>✕ Reject</button>
              </div>
            )}
          </div>

          <div style={{ marginTop: 12, borderTop: '1px solid #F1F5F9', paddingTop: 10 }}>
            {diffRows(cr).map(({ k, before, after }) => (
              <div key={k} style={{ display: 'flex', gap: 10, fontSize: 13, padding: '3px 0', alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span style={{ minWidth: 130, color: '#64748B', fontWeight: 600 }}>{k}</span>
                {cr.type === 'menu_add' ? (
                  <span style={{ color: '#166534' }}>{fmt(k, after)}</span>
                ) : cr.type === 'menu_delete' ? (
                  <span style={{ color: '#B91C1C', textDecoration: 'line-through' }}>{fmt(k, before)}</span>
                ) : (
                  <>
                    <span style={{ color: '#B91C1C' }}>{fmt(k, before)}</span>
                    <span style={{ color: '#94A3B8' }}>→</span>
                    <span style={{ color: '#166534', fontWeight: 600 }}>{fmt(k, after)}</span>
                  </>
                )}
              </div>
            ))}
            {cr.applyError ? <div style={{ color: '#B91C1C', fontSize: 12, marginTop: 6 }}>⚠ Last apply failed: {cr.applyError}</div> : null}
            {cr.reviewNote ? <div style={{ color: '#64748B', fontSize: 12, marginTop: 6 }}>Note: {cr.reviewNote}</div> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

const btn = (bg, small) => ({ background: bg, color: '#fff', border: 'none', borderRadius: 8, padding: small ? '8px 12px' : '10px 16px', fontSize: small ? 13 : 14, fontWeight: 600, cursor: 'pointer' });
const chip = (bg = '#F1F5F9', color = '#334155') => ({ background: bg, color, fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 6, marginLeft: 8 });
const inp = { padding: '9px 12px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };

export default ChangeRequestManagement;
